import { useEffect, useMemo, useState } from 'react';

import { DisplayTimeZone, getLearnerClasses } from '../api/learner';
import { UTC_ZONE } from '../time/zonedTime';

export { joinStateAt } from './joinWindow';

/**
 * Time on the learner's screens.
 *
 * Two rules from the case: the instant is the server's and the zone is the
 * learner's or the household's — labelled, and never silently the browser's
 * (STU-16-FR-03, S07-CAL-03) — and the device clock is not a time source
 * (UC-S08 SR-02). The countdown therefore runs on the server's clock, carried
 * forward from the `serverNow` of the last response.
 */

// One formatter for every family screen, the learner's and the parent's.
export { UTC_ZONE, dayKey, formatDay, formatDayAndTime, formatTime } from '../time/zonedTime';

/** The sentence every screen with times shows once, so nobody guesses whose clock it is. */
export function zoneLabel(tz: DisplayTimeZone): string {
  switch (tz.source) {
    case 'LEARNER':
      return `Las horas están en tu hora (${tz.zone}).`;
    case 'HOUSEHOLD':
      return `Las horas están en la hora de tu casa (${tz.zone}).`;
    default:
      return 'Las horas están en hora UTC.';
  }
}

/**
 * The server's "now", moving. `serverNow` is the instant the server answered;
 * the device clock only measures how long ago that was.
 */
export function useServerClock(serverNow: string | undefined, tickMs = 1000): Date {
  const offset = useMemo(
    () => (serverNow ? new Date(serverNow).getTime() - Date.now() : 0),
    [serverNow],
  );
  const [now, setNow] = useState(() => new Date(Date.now() + offset));

  useEffect(() => {
    setNow(new Date(Date.now() + offset));
    const id = setInterval(() => setNow(new Date(Date.now() + offset)), tickMs);
    return () => clearInterval(id);
  }, [offset, tickMs]);

  return now;
}

/** "1 hora y 5 minutos", "3 minutos", "menos de un minuto" — for a child, not a stopwatch. */
export function waitInWords(fromMs: number): string {
  if (fromMs < 60_000) return 'menos de un minuto';
  const totalMinutes = Math.ceil(fromMs / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  if (days >= 1) return days === 1 ? '1 día' : `${days} días`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const h = hours === 1 ? '1 hora' : `${hours} horas`;
  const m = minutes === 1 ? '1 minuto' : `${minutes} minutos`;
  if (hours === 0) return m;
  return minutes === 0 ? h : `${h} y ${m}`;
}

/**
 * The learner's display zone for screens whose own data carries none (past
 * classes, progress). Read from the same server envelope "Mis clases" uses;
 * until it arrives, or if it cannot be read, times are drawn in UTC and the
 * label says so — never in the browser's zone.
 */
export function useLearnerTimeZone(studentId: string | null): DisplayTimeZone {
  const [tz, setTz] = useState<DisplayTimeZone>(UTC_ZONE);
  useEffect(() => {
    if (!studentId) return;
    let live = true;
    getLearnerClasses(studentId)
      .then((res) => {
        if (live && res?.timeZone) setTz(res.timeZone);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [studentId]);
  return tz;
}
